import React, { useState } from 'react';

/**
 * InfoHint — a small "(i)" icon that reveals a plain-language explanation
 * on hover/focus/tap. Used next to flags and settings toggles so staff
 * understand what each option does without technical jargon.
 *
 * Props:
 *   text        — the explanation to show (required)
 *   label       — accessible label (defaults to "More info")
 *   side        — 'top' | 'bottom' (tooltip position; default 'top')
 *
 * Styling follows the day/night contrast rules: solid backgrounds in both
 * modes, never a transparent surface that vanishes on a tinted parent.
 */
export default function InfoHint({ text, label = 'More info', side = 'top' }) {
  const [open, setOpen] = useState(false);

  if (!text) return null;

  const pos =
    side === 'bottom'
      ? 'top-full mt-1'
      : 'bottom-full mb-1';

  return (
    <span className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label}
        title={text}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full
                   border border-slate-400 dark:border-slate-500
                   bg-white dark:bg-slate-800
                   text-[10px] font-bold leading-none
                   text-slate-600 dark:text-slate-200
                   hover:bg-slate-100 dark:hover:bg-slate-700
                   cursor-help select-none"
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          className={`absolute left-1/2 -translate-x-1/2 ${pos} z-50 w-60
                      rounded-md px-3 py-2 text-xs leading-snug font-normal
                      normal-case text-left shadow-lg
                      bg-slate-900 text-slate-50
                      dark:bg-slate-700 dark:text-slate-50`}
          style={{ whiteSpace: 'normal' }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
